import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import SchoolIcon from "@mui/icons-material/School";
import BadgeIcon from "@mui/icons-material/Badge";
import { useLocation } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { getUserProfile } from "../../services/userService";
import { updateMyProfile } from "../../services/studentService";
import { uploadAvatar } from "../../services/images";
import AvatarUpload from "../../components/AvatarUpload";
import { useHandleError } from "../../hooks/useHandleError";

const ProfilePage: React.FC = () => {
  const handleError = useHandleError();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<any>(null);

  // Student self-service edit state (photo + address only — names/email/
  // school are locked: names feed certificates, school anchors visibility)
  const isStudent = location.pathname.startsWith("/student");
  const [editValues, setEditValues] = useState({
    photoUrl: "",
    city: "",
    country: "",
    currentLocation: "",
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setLoading(true);
    getUserProfile()
      .then((res) => {
        setProfile(res.data);
        setEditValues({
          photoUrl: res.data?.photoUrl || "",
          city: res.data?.city || "",
          country: res.data?.country || "",
          currentLocation: res.data?.currentLocation || "",
        });
      })
      .catch(handleError)
      .finally(() => setLoading(false));
  }, []);

  const handleEditChange = (field: string, value: string) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleAvatarUpload = (file: File) => {
    uploadAvatar(file)
      .then((response) => handleEditChange("photoUrl", response.imageUrl))
      .catch((error) =>
        enqueueSnackbar(error?.response?.data || "Could not upload image.", {
          variant: "error",
        })
      );
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await updateMyProfile(editValues);
      setProfile((prev: any) => ({ ...prev, ...response.data }));
      setDirty(false);
      enqueueSnackbar("Profile updated.", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message || "Could not update profile.",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Could not load profile.</Typography>
      </Box>
    );
  }

  // Derive display values from any role's profile shape
  const name =
    profile.name ||
    [profile.firstName, profile.lastName].filter(Boolean).join(" ") ||
    profile.email;
  const email = profile.email || "";
  const mobile = profile.mobile || "";
  const city = profile.city || "";
  const country = profile.country || "";
  const role = profile.role || "";
  const status = profile.status || "";
  const schoolName = profile.schoolName || profile.currentSchool || "";
  const photoUrl = profile.photoUrl || "";
  const isActive = profile.isActive;

  const initials = name
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <Box sx={{ p: 3, maxWidth: 700, mx: "auto" }}>
      <Typography variant="h5" gutterBottom>
        My Profile
      </Typography>

      <Card>
        <CardContent>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              mb: 3,
            }}
          >
            <Avatar
              src={photoUrl}
              sx={{
                width: 80,
                height: 80,
                fontSize: 32,
                bgcolor: "primary.main",
              }}
            >
              {!photoUrl && initials}
            </Avatar>
            <Box>
              <Typography variant="h5">{name}</Typography>
              {role && (
                <Chip
                  label={role}
                  size="small"
                  color="primary"
                  sx={{ mt: 0.5 }}
                />
              )}
              {status && (
                <Chip
                  label={status}
                  size="small"
                  variant="outlined"
                  sx={{ mt: 0.5, ml: 1 }}
                />
              )}
              {isActive === false && (
                <Chip
                  label="DISABLED"
                  size="small"
                  color="error"
                  sx={{ mt: 0.5, ml: 1 }}
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Details */}
          <Grid container spacing={2}>
            {email && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <EmailIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Email
                    </Typography>
                    <Typography variant="body2">{email}</Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {mobile && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PhoneIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Mobile
                    </Typography>
                    <Typography variant="body2">{mobile}</Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {(city || country) && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocationCityIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Location
                    </Typography>
                    <Typography variant="body2">
                      {[city, country].filter(Boolean).join(", ")}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {schoolName && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SchoolIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      School
                    </Typography>
                    <Typography variant="body2">{schoolName}</Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* Student-specific: Placement test result */}
          {profile.placementTest?.result && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Placement Test Result
              </Typography>
              <Box sx={{ display: "flex", gap: 3 }}>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Program
                  </Typography>
                  <Typography variant="body2">
                    {profile.placementTest.result.programName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Easy Correct
                  </Typography>
                  <Typography variant="body2">
                    {profile.placementTest.result.easyCorrect}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Hard Correct
                  </Typography>
                  <Typography variant="body2">
                    {profile.placementTest.result.hardCorrect}
                  </Typography>
                </Box>
              </Box>
            </>
          )}

          {/* Student-specific: Classroom info */}
          {profile.myClassroom && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Enrolled Classroom
              </Typography>
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Program
                  </Typography>
                  <Typography variant="body2">
                    {profile.myClassroom.programName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Teacher
                  </Typography>
                  <Typography variant="body2">
                    {profile.myClassroom.teacher?.name || "N/A"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Progress
                  </Typography>
                  <Typography variant="body2">
                    {profile.myClassroom.courseProgress?.overallProgress || 0}%
                  </Typography>
                </Box>
              </Box>
            </>
          )}

          {/* Teacher-specific: Classrooms */}
          {profile.classrooms && profile.classrooms.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Assigned Classrooms
              </Typography>
              <Typography variant="body2">
                {profile.classrooms.length} classroom
                {profile.classrooms.length > 1 ? "s" : ""}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      {/* Student self-service: photo + address. Name/email/school changes
          go through the teacher/admin (names are printed on certificates). */}
      {isStudent && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Edit Profile
            </Typography>
            <Typography variant="caption" color="textSecondary">
              You can update your photo and location. To change your name,
              email or school, please contact your teacher.
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <AvatarUpload
                  photoUrl={editValues.photoUrl}
                  logoText="Profile photo"
                  onSubmit={handleAvatarUpload}
                  onRemove={() => handleEditChange("photoUrl", "")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="City"
                  value={editValues.city}
                  onChange={(e) => handleEditChange("city", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Country"
                  value={editValues.country}
                  onChange={(e) => handleEditChange("country", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Current location"
                  value={editValues.currentLocation}
                  onChange={(e) =>
                    handleEditChange("currentLocation", e.target.value)
                  }
                />
              </Grid>
            </Grid>

            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={handleSaveProfile}
              disabled={!dirty || saving}
            >
              {saving ? "Saving..." : "Save changes"}
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ProfilePage;
